import React, { useState, useEffect } from "react";
import { McCard } from "@maersk-global/mds-react-wrapper/components-core/mc-card";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { useStore } from "../../store/store.context";
import { CHAT_CONSTANTS } from "../../constants/constants";
import { fetchCaseSummary } from "../../services/case-summary-service";
import { API_URL, CORE_URL } from "../../services/api";
import { McModal } from "@maersk-global/mds-react-wrapper/components-core/mc-modal";
import { McSelect } from "@maersk-global/mds-react-wrapper/components-core/mc-select";
import { McOption } from "@maersk-global/mds-react-wrapper/components-core/mc-option";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import * as XLSX from "xlsx";
import { McDateRange } from "@maersk-global/mds-react-wrapper/components-core/mc-date-range";



import "./prompt-panel.scss";
import { rowHeightWarning } from "@mui/x-data-grid/hooks/features/rows/gridRowsUtils";

const PromptPanel = () => {
  const { store, updateStore } = useStore();
  const [caseCounts, setCaseCounts] = useState({
    open: 0,
    approved: 0,
    rejected: 0,
    onHold: 0,
    compliant: 0,
    nonCompliant: 0,
    exception: 0,
    total: 0,
    closedAfrCases: 0
  });
  const [loading, setLoading] = useState(true);
  
  // Accuracy Modal state
  const [accuracyModalOpen, setAccuracyModalOpen] = useState(false);
  const [accuracyDate, setAccuracyDate] = useState({ from: "", to: "" });
  const [accuracyDateKey, setAccuracyDateKey] = useState(0);
  const [accuracyCountry, setAccuracyCountry] = useState("");
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
const [minDate, setMinDate] = useState(null);
const [maxDate, setMaxDate] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadCaseSummary = async () => {
      try {
        setLoading(true);
        const counts = await fetchCaseSummary();
        if (!mounted) return;
        setCaseCounts(counts);
      } catch (error) {
        console.error('Error loading case summary:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // Fetch when the prompt panel is visible or when Cargo Mate tab is active.
    // This ensures counts are refreshed when navigating back to the sample prompts
    // or when the header/tab control updates `activeTab`.
    if (store && (store.prompt || store.activeTab === 'cargo-mate')) {
      loadCaseSummary();
    }

    // Fallback: some components dispatch a global event 'backToSamplePrompts'
    // (see Header.js). Listen to it and refresh counts when fired.
    const onBackToSamplePrompts = () => {
      loadCaseSummary();
    };
    window.addEventListener('backToSamplePrompts', onBackToSamplePrompts);

    return () => {
      mounted = false;
      window.removeEventListener('backToSamplePrompts', onBackToSamplePrompts);
    };
  }, [store && store.prompt, store && store.activeTab]);

  const getCountForPrompt = (title) => {
    const titleLower = title.toLowerCase();
    console.log('[getCountForPrompt] Looking up count for:', title, '| caseCounts:', caseCounts);
    if (titleLower.includes('summary')) return caseCounts.total;
    if (titleLower.includes('approved')) return caseCounts.approved;
    if (titleLower.includes('rejected')) return caseCounts.rejected;
    if (titleLower.includes('on-hold') || titleLower.includes('hold')) return caseCounts.onHold;
    if (titleLower.includes('open')) return caseCounts.open;
    if (titleLower.includes('compliant') && !titleLower.includes('non')) return caseCounts.compliant;
    if (titleLower.includes('non-compliant') || titleLower.includes('non compliant')) return caseCounts.nonCompliant;
    if (titleLower.includes('closed afr cases')) return caseCounts.closedAfrCases;
    return null;
  };

 const fetchDownloadReport = async () => {
  try {
    console.log("Fetching from:", `${API_URL}/getapprovedfreightcases`); 
    const response = await fetch(`${API_URL}/getapprovedfreightcases`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch closed AFR cases: ${response.statusText}`);
    }
    const data = await response.json();
    console.log("API response:", JSON.stringify(data, null, 2));
    if (!data.body || !data.body.data) {
      throw new Error("Invalid API response format: Missing body.data");
    }
    console.log("Successfully downloaded the report data, number of records:", data.body.data.length);
    return data.body.data;
  } catch (error) {
    console.error("Error fetching report:", error);
    throw error;
  }
};

const triggerDownloadReport = async () => {
  console.log("Download button clicked");
  try {
    const data = await fetchDownloadReport();
    if (!data.length) {
      console.log("No data to export");
      updateStore({ errorMessage: "No data available for export." });
      return;
    }

    // Define ALL headers - Complete list
    const headers = [[
      // Basic Case Information
      "Case Number",
      "Status",
      "Case Status Reason",
      "Date",
      "Time",
      
      // BL & Invoice Information
      "BL Number",
      "Related Invoices",
      
      // Customer Information
      "Customer Name",
      "Customer ID",
      "Customer Bank Account",
      "Country",
      
      // Transaction Information
      "Transaction ID",
      "Value Date",
      "Execution Date",
      "Currency",
      "Payment Amount",
      
      // Shipment Details
      "Direction",
      "Shipment Type",
      
      // Beneficiary Information
      "Beneficiary Name",
      "Account Number",
      "Account Type",
      "Beneficiary Account",
      
      // Payment Information
      "Payment Type",
      "Payment Status",
      "Payment Date",
      "Proof of Payment",
      "Proof Document Path",
      "Proof of Payment ID",
      
      // Document Information
      "Document Type",
      
      // Validation Rules (3 rules max)
      "Rule 1",
      "Rule 1 AFR Data",
      "Rule 1 Booking Data",
      "Rule 2",
      "Rule 2 AFR Data",
      "Rule 2 Booking Data",
      "Rule 3",
      "Rule 3 AFR Data",
      "Rule 3 Booking Data",
      
      // SLA & Processing Information
      "SLA Status Name",
      "SLA Remaining Time",
      "Country SLA Time",
      "Shift Start Time",
      
      // Rule & Processing Details
      "Rule Name",
      "Rule Iteration",
      "Outcome",
      "AFR Data",
      "Booking Data",
      
      // Communication & Tracking
      "Response Received",
      "Remarks",
      "Reason",
      "Region",
      "Priority",
      
      // Dates
      "Created Date",
      "Modified Date",
      "Case Received DateTime",
      "Case Processing DateTime",
      
      // Assignment
      "Case Assigned To",
    ]];

    // Deduplicate data by caseId to avoid duplicate case numbers
    const uniqueCases = {};
    data.forEach(item => {
      if (!uniqueCases[item.caseId]) {
        uniqueCases[item.caseId] = item;
      }
    });
    const uniqueData = Object.values(uniqueCases);

    // Map data to match headers
    const formattedData = uniqueData.map(item => {
      // Extract nested data
      const blNumber = item.blDetails?.[0]?.blNumber || "";
      const relatedInvoices = item.blDetails?.[0]?.relatedInvoices?.join("; ") || "";
      const proofdocumentpath = item.popDocuments?.[0]?.link || "";
      
      // Extract validation status rules (max 3 rules)
      const validationRules = item.validationStatus || [];
      const rule1 = validationRules[0]?.Rule || "";
      const rule1AfrData = validationRules[0]?.AFR_data || "";
      const rule1BookingData = validationRules[0]?.Booking_data || "";
      const rule2 = validationRules[1]?.Rule || "";
      const rule2AfrData = validationRules[1]?.AFR_data || "";
      const rule2BookingData = validationRules[1]?.Booking_data || "";
      const rule3 = validationRules[2]?.Rule || "";
      const rule3AfrData = validationRules[2]?.AFR_data || "";
      const rule3BookingData = validationRules[2]?.Booking_data || "";

      return [
        // Basic Case Information
        item.caseId || "",
        item.status || "",
        item.casestatusreason || "",
        item.date || "",
        item.time || "",
        
        // BL & Invoice
        blNumber,
        relatedInvoices,
        
        // Customer
        item.customerName || "",
        item.customerId || "",
        item.customerBankAccount || "",
        item.countryCode || "",
        
        // Transaction
        item.transactionId || "",
        item.valueDate || "",
        item.executionDate || "",
        item.currency || "",
        item.amount || "",
        
        // Shipment
        item.direction || "",
        item.direction || "", // Note: shipmentType might be same as direction
        
        // Beneficiary
        item.beneficiaryName || "",
        item.accountNo || "",
        item.accountType || "",
        item.beneficiaryaccount || item.beneficiaryAccount || "", // Handle both camelCase and snake_case
        
        // Payment
        item.paymentType || "",
        item.paymentStatus || "",
        item.paymentdate || "",
        item.proofOfPayment || 0,
        proofdocumentpath,
        item.popId || "",
        
        // Document
        item.documentType || "",
        
        // Validation Rules
        rule1,
        rule1AfrData,
        rule1BookingData,
        rule2,
        rule2AfrData,
        rule2BookingData,
        rule3,
        rule3AfrData,
        rule3BookingData,
        
        // SLA & Processing
        item.slaStatusName || "",
        item.slaRemainingTime || "",
        item.countrySlaTime || "",
        item.shiftStartTime || "",
        
        // Rule & Processing Details
        item.ruleName || "",
        item.ruleIteration || "",
        item.outcome || "",
        item.afrData || "",
        item.bookingData || "",
        item.reason || "",
        
        // Communication & Tracking
        item.responseReceived || "",
        item.remarks || "",
        item.reason || "",
        item.region || "",
        item.priority || "",
        
        // Dates
        item.createdDate || "",
        item.modifiedDate || "",
        item.caseReceivedDateTime || "",
        item.caseProcessingDateTime || "",
        
        // Assignment
        item.caseAssignedTo || "",
      ];
    });

    console.log("Formatted data sample:", formattedData.slice(0, 2));

    // Set responsive column widths
    const columnWidths = [
      { wch: 15 }, // Case Number
      { wch: 12 }, // Status
      { wch: 18 }, // Case Status Reason
      { wch: 15 }, // Date
      { wch: 12 }, // Time
      { wch: 15 }, // BL Number
      { wch: 20 }, // Related Invoices
      { wch: 20 }, // Customer Name
      { wch: 12 }, // Customer ID
      { wch: 18 }, // Customer Bank Account
      { wch: 15 }, // Country
      { wch: 15 }, // Transaction ID
      { wch: 12 }, // Value Date
      { wch: 12 }, // Execution Date
      { wch: 10 }, // Currency
      { wch: 15 }, // Payment Amount
      { wch: 12 }, // Direction
      { wch: 15 }, // Shipment Type
      { wch: 18 }, // Beneficiary Name
      { wch: 15 }, // Account Number
      { wch: 14 }, // Account Type
      { wch: 18 }, // Beneficiary Account
      { wch: 14 }, // Payment Type
      { wch: 14 }, // Payment Status
      { wch: 12 }, // Payment Date
      { wch: 15 }, // Proof of Payment
      { wch: 25 }, // Proof Document Path
      { wch: 15 }, // Proof of Payment ID
      { wch: 15 }, // Document Type
      { wch: 20 }, // Rule 1
      { wch: 20 }, // Rule 1 AFR Data
      { wch: 20 }, // Rule 1 Booking Data
      { wch: 20 }, // Rule 2
      { wch: 20 }, // Rule 2 AFR Data
      { wch: 20 }, // Rule 2 Booking Data
      { wch: 20 }, // Rule 3
      { wch: 20 }, // Rule 3 AFR Data
      { wch: 20 }, // Rule 3 Booking Data
      { wch: 18 }, // SLA Status Name
      { wch: 18 }, // SLA Remaining Time
      { wch: 15 }, // Country SLA Time
      { wch: 15 }, // Shift Start Time
      { wch: 15 }, // Rule Name
      { wch: 14 }, // Rule Iteration
      { wch: 15 }, // Outcome
      { wch: 20 }, // AFR Data
      { wch: 20 }, // Booking Data
      { wch: 15 }, // Response Received
      { wch: 25 }, // Remarks
      { wch: 15 }, // Reason
      { wch: 12 }, // Region
      { wch: 12 }, // Priority
      { wch: 15 }, // Created Date
      { wch: 15 }, // Modified Date
      { wch: 18 }, // Case Received DateTime
      { wch: 18 }, // Case Processing DateTime
      { wch: 15 }, // Case Assigned To
    ];

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...formattedData]);
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Closed AFR Cases");
    XLSX.writeFile(workbook, "closed_afr_cases_report.xlsx");

    console.log("Report downloaded successfully");
  } catch (error) {
    console.error("Error downloading report:", error);
    updateStore({ errorMessage: `Failed to download report: ${error.message}` });
  }
};

const openAccuracyReportModal = async () => {
  try {
    setAccuracyModalOpen(true);
    setLoadingCountries(true);
    setAccuracyDate({ from: "", to: "" });
    const rawUserInfo = sessionStorage.getItem('userInfo');
    const userEmail = rawUserInfo ? (JSON.parse(rawUserInfo).email || '') : '';
    const optionsUrl = userEmail
      ? `${API_URL}/accuracy-report/options?user_email=${encodeURIComponent(userEmail)}`
      : `${API_URL}/accuracy-report/options`;
    const resp = await fetch(optionsUrl);
    if (!resp.ok) throw new Error("Failed to fetch country options");
    const data = await resp.json();
    const countries = Array.isArray(data.countries) ? data.countries : (data.body?.countries || []);
    setCountryOptions(["All countries", ...countries]);
    setMinDate(data.min_modifieddate || null);
    setMaxDate(data.max_modifieddate || null);
  } catch (e) {
    console.error("Failed to load countries for accuracy modal:", e);
    setCountryOptions([]);
  } finally {
    setLoadingCountries(false);
  }
};

const handleAccuracyModalClose = () => {
  setAccuracyModalOpen(false);
  setAccuracyDate({ from: "", to: "" });
  setAccuracyCountry("");
  setAccuracyDateKey((k) => k + 1); // force remount to clear UI state
};

const handleAccuracyModalSubmit = async () => {
  try {
    if (!accuracyDate || !accuracyCountry) {
      updateStore({ errorMessage: "Please select both date and country." });
      return;
    }

    // Fetch full accuracy data from API service
    const baseUrl = `${API_URL}/accuracy-report?from=${encodeURIComponent(accuracyDate?.from)}&to=${encodeURIComponent(accuracyDate?.to)}`;
    const urlnew = accuracyCountry === "All countries"
      ? baseUrl
      : `${baseUrl}&country_name=${encodeURIComponent(accuracyCountry)}`;
    const resp = await fetch(urlnew, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!resp.ok) {
      const errText = await resp.text();
      throw new Error(`Failed to fetch accuracy report: ${resp.status} ${errText}`);
    }
    const rows = await resp.json();
console.log("Fetched:", rows);
    // Robust client-side filter using selected date and country
    const normalizeStr = (s) => String(s || "").trim().toLowerCase();
    const extractYmd = (v) => {
      if (!v) return "";
      if (typeof v === "string") {
        const m = v.match(/\b(\d{4}-\d{2}-\d{2})\b/);
        if (m) return m[1];
      }
      try {
        const d = new Date(v);
        if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      } catch (_) {}
      return "";
    };



    // Prepare XLSX export
    const headers = [[
      "Country Name",
      "Case Number",
      "Current Case Status",
      "Accuracy",
      "Comment",
      "Added By",
      "Modified Date",
      "Case Status",
    ]];

    const dataRows = rows.map((r) => [
      r?.country_name ?? "",
      r?.casenumber ?? "",
      r?.current_casestatus ?? "",
      r?.accuracy ?? "",
      r?.comment ?? "",
      r?.addedby ?? "",
      r?.modifieddate ?? "",
      r?.casestatus ?? "",
    ]);

    console.log("Preparing to export rows:", dataRows);

    const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...dataRows]);
    worksheet['!cols'] = [
      { wch: 18 }, // Country Name
      { wch: 16 }, // Case Number
      { wch: 22 }, // Current Case Status
      { wch: 10 }, // Accuracy
      { wch: 24 }, // Comment
      { wch: 16 }, // Added By
      { wch: 20 }, // Modified Date
      { wch: 16 }, // Case Status
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Accuracy Report");
    const safeCountry = accuracyCountry.replace(/[^A-Za-z0-9-_]/g, "_");
    const datePart = `${accuracyDate?.from || ""}_${accuracyDate?.to || ""}`.replace(/[^A-Za-z0-9-_]/g, "_");
    // Write workbook to ArrayBuffer and download via Blob (consistent with CSV approach)
    const wbArray = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `accuracy_report_${safeCountry}_${datePart}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    handleAccuracyModalClose();
  } catch (error) {
    console.error("Error downloading accuracy report:", error);
    updateStore({ errorMessage: `Failed to download accuracy report: ${error.message}` });
  }
};

  // const handlePrompt = (query) => {
  //   updateStore({ 
  //     promptQuery: query, 
  //     prompt: false, 
  //     loading: true,
  //     apiResponseData: null // Clear previous data
  //   });
  // };

      const handlePrompt = (query, title) => {
      // Check if the prompt is for downloading the AFR cases report
      if (title && title.toLowerCase().includes('closed afr cases')) {
        triggerDownloadReport();
        return;
      }
        if (title && (title.toLowerCase().includes('accuracy report') || title.toLowerCase().includes('download accuracy'))) {
        openAccuracyReportModal();
        return;
      }
      updateStore({ 
        promptQuery: query,
        lastRefreshableQuery: query, // persist for Refresh (not cleared on back-navigation)
        promptQueryNonce: (store.promptQueryNonce || 0) + 1, // force re-run for identical query
        prompt: false, 
        loading: true,
        apiResponseData: null, // Clear previous data
        dashboardStatus: "" // reset any status filter that could hide results
      });
    };

  // const renderCategoryCards = (category) => {
  //   return category.prompts.map((prompt, index) => {
  //     const count = getCountForPrompt(prompt.title);

  //     return (
  //       <div
  //         key={index}
  //         className="promptCard box"
  //         onClick={() => {
  //           // Redirect to Advanced Case Search only for "User Specific Cases" under "Specific Case Details"
  //           if (
  //             category.title === "Specific Case Details" &&
  //             prompt.title === "User Specific Cases"
  //           ) {
  //             updateStore({ activeTab: "advanced-search" });
  //           } else {
  //             handlePrompt(prompt.query);
  //           }
  //         }}
  //       >
  //         <McCard>
  //           <div className="promptSection">
  //             <div style={{ "margin-right": "8px", "padding": "2px", backgroundColor: prompt.bgColor }}>
  //             <McIcon icon={prompt.icon} color={prompt.color} size="24"></McIcon>
  //             </div>
  //             <div className="promptContent">
  //               <div className="promptTitleContainer">
  //                 <span className="promptTitle">{prompt.title}</span>
  //                 {/* Only show counts or loading indicator when the prompt wants a count displayed */}
  //                 {prompt.displayCount && (
  //                   <>
  //                     {count !== null && !loading && (
  //                       <span className="promptCount">{count}</span>
  //                     )}
  //                     {loading && (
  //                       <span className="promptCount">Loading Count...</span>
  //                     )}
  //                   </>
  //                 )}
  //               </div>
  //               <div className="promptDescription">{prompt.description}</div>
  //             </div>
  //             <McIcon
  //               icon="arrow-right"
  //               color={"#1686BD"}
  //               className="promptArrow"
  //               size="24"
  //             ></McIcon>
  //           </div>
  //         </McCard>
  //       </div>
  //     );
  //   });
  // };


  const renderCategoryCards = (category) => {
  return category.prompts.map((prompt, index) => {
    const count = getCountForPrompt(prompt.title);

  // Apply a subtle blue shadow only for the "Backlog Case Summary" card
  const promptCardStyle = (prompt.title === 'Backlog Case Summary') ? { boxShadow: '0 4px 12px #1686BD' } : {};
  // Apply blue color to the title text only for the "Backlog Case Summary" card
  const promptTitleStyle = (prompt.title === 'Backlog Case Summary') ? { color: '#1686BD' } : {};

    return (
      <div
        key={index}
        className="promptCard box"        
        onClick={() => {
          // Redirect to Advanced Case Search only for "User Specific Cases" under "Specific Case Details"
          if (
            category.title === "Specific Case Details" &&
            prompt.title === "User Specific Cases"
          ) {
            updateStore({ activeTab: "advanced-search" });
          } else {
            handlePrompt(prompt.query, prompt.title); // Pass the title to handlePrompt
          }
        }}
      >
        <McCard style={promptCardStyle}>
          <div className="promptSection">            
            <div className="promptContent">
              <div className="promptTitleContainer">
                <div style={{display: 'flex'}}>
                  <div style={{ marginRight: "5px", backgroundColor: prompt.bgColor }}>
                    <McIcon icon={prompt.icon} color={prompt.color} size="24" className="sampleIcon"></McIcon>
                  </div>
                  <span className="promptTitle" style={promptTitleStyle}>{prompt.title}</span>  
                 
                </div>
                {prompt.displayCount && (
                  <>
                    {count !== null && !loading && (
                      <span className="promptCount">{count}</span>
                    )}
                    {loading && (
                      <span className="loadingCount">Loading Count</span>
                    )}
                  </>
                )}
                 <McIcon
                    icon="arrow-right"
                    color={"#1686BD"}
                    className="promptArrow"
                    size="24"
                  ></McIcon>
              </div>
              <div className="promptDescription">{prompt.description}</div>
            </div>            
          </div>
        </McCard>
      </div>
    );
  });
};

                    // Even if no rows match, still create an XLSX with headers (empty data)
  //       <div className="categoryHeader">
  //         {/* <McIcon
  //           className="categoryIcon"
  //           icon={category.icon}
  //           color={category.color}
  //           size="24"
  //         /> */}
  //         <span className="categoryTitle">{category.title}</span>
  //       </div>
  //       <div className="categoryPrompts">
  //         {renderCategoryCards(category)}
  //       </div>
  //     </div>
  //   ));
  // };


  const renderCategories = () => {
  return CHAT_CONSTANTS.PROMPT_CATEGORIES
    .filter(category => category.title !== "Validation Outcomes")
    .map((category, categoryIndex) => (
      <div key={categoryIndex} className="categorySection">
        <div className="categoryHeader">
          <span className="categoryTitle">{category.title}</span>
        </div>
        <div className="categoryPrompts">
          {renderCategoryCards(category)}
        </div>
      </div>
    ));
};


  return (
    <div className="promptPane">
      <McCard>
        <div className="promptContainer">
          <div className="promptHeaderSection">
            <span className="heading">Sample Prompts</span>
            <span className="subHeading">
             <i> Get started with the digital assistant by exploring the varied
              sample prompts, or by asking a question below.</i>
            </span>
          </div>
          <div className="mds-container">
            <div className="promptsContainerSection">
              {renderCategories()}
            </div>
          </div>
        </div>
      </McCard>

      {/* Download Accuracy Report Modal */}
      <McModal
        heading="Download Accuracy Report"
        open={accuracyModalOpen}
        closed={handleAccuracyModalClose}
        width="40vw"
        height="auto"
        escapecloseactiondisabled
        backdropcloseactiondisabled
      >
        <div style={{ padding: '20px', minWidth: '360px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <McDateRange
              label="Date: *"
              type="date"
              name="checkin-date"
              key={accuracyDateKey}
  click={(e) => setAccuracyDate(e.target.value)}
  fromlabel="From"
  tolabel="To"
  legend="Please enter a date range"

              placeholder="YYYY-MM-DD"
              value={accuracyDate}

            />
            <McSelect
             listsearch={true}
              label={loadingCountries ? "Country: (loading...)" : "Country:"}
              placeholder={loadingCountries ? "Loading countries..." : "Select country"}
              value={accuracyCountry}
              name="accuracyCountry"
              optionselected={(e) => setAccuracyCountry(e.target.value)}
              disabled={loadingCountries}
            >
              {countryOptions.map((c) => (
                <McOption key={c} value={c}>{c}</McOption>
              ))}
            </McSelect>
          </div>
        </div>

        <McButton
          slot="primaryAction"
          appearance="primary"
          fit="medium"
          dialogaction="ok"
          click={handleAccuracyModalSubmit}
          disabled={!(accuracyDate?.from && accuracyDate?.to) || !accuracyCountry}
        >
          Continue
        </McButton>

        <McButton
          slot="secondaryAction"
          appearance="neutral"
          variant="filled"
          dialogaction="cancel"
          fit="medium"
          click={handleAccuracyModalClose}
        >
          Cancel
        </McButton>
      </McModal>
    </div>
  );
};

export default PromptPanel;
 